import { useState, useRef, useEffect } from "react";
import { FlashMode } from "../Types";
import { Grid2 as Grid } from "@mui/material";
import { useDrop, useDrag } from "react-dnd";
import { Identifier } from "dnd-core";
import { useGlobalContext } from "../Context/GlobalContext";
import Flash from "./flash";
import { emptyMode } from "../Types";
import { useDragLayer } from 'react-dnd';
import { unifiedModes } from "../Lib/Devices";

interface DropResult {
    active: Boolean;
    isDustbin: Boolean;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
    active: Boolean;
}

export const DeviceModes = ({
    steadyModes,
    flashModes,
    handleDrop,
    handleCannotDrop,
    onClick,
    deviceType,
    lastConfigured
}: {
    steadyModes: FlashMode[];
    flashModes: FlashMode[];
    handleDrop: Function;
    handleCannotDrop: Function;
    onClick: Function;
    deviceType: number;
    lastConfigured: string
}) => {
    const ModeGrid = ({type}: {type: string}) => {
        const modes = type === 'steady' ? 
        [...steadyModes, ...(steadyModes.length < 4 ? [emptyMode] : [])] 
        : [...flashModes, ...(flashModes.length < (unifiedModes(deviceType) ? 8 : 4) ? [emptyMode] : [])];

        const paddingValues: any = {
            1: '6% 0 6% 0',
            3: '8% 0 8% 0',
            4: '6% 0 6% 0',
            5: '1% 0 1% 0',
            6: '1% 0 1% 0',
            7: '8% 0 8% 0',
            8: '8% 0 8% 0',
        }

        const padding = (
            type === 'flash' && (flashModes.length === 0 || flashModes.length === 4)) 
            || (type === 'steady' && steadyModes.length === 0) 
        ? paddingValues[deviceType] : '0 0 0 0';

        return (
            <Grid container spacing={2} sx={{ margin: "13px 0" }}>
                {modes?.map((mode: FlashMode, index: number) => (
                    mode.id !== 0 ?
                    <Grid size={{ xs: 4, lg: 3 }} key={mode.instance || `${mode.id}-${index}`}>
                        <Mode
                            mode={mode}
                            active={true}
                            hasActive={lastConfigured === mode.instance}
                            handleDrop={handleDrop}
                            onClick={onClick}
                            deviceType={deviceType}
                        />
                    </Grid> :
                    <Dustbin 
                        key={`dustbin-${type}-${index}`} 
                        handleCannotDrop={handleCannotDrop}
                        type={type}
                        padding={padding}
                    />
                ))}
            </Grid>
        );
    }

    return (
        <>
            {!unifiedModes(deviceType) && <ModeGrid type={'steady'}/>}
            <ModeGrid type={'flash'}/>
        </>
    );
}

const Dustbin = (
    { type, handleCannotDrop, padding }: 
    { type: string; handleCannotDrop: Function, padding: string }
) => {
    const ref = useRef<HTMLDivElement>(null);
    const { getThemeStyles } = useGlobalContext();
    const styles = getThemeStyles();
    const [allowedDrop, setAllowedDrop] = useState<boolean>(false);

    const isSameType = (item: any) => {
        if (item.flashMode?.mode_type === 'steady' && type === 'flash') {
            return false;
        } else if (item.flashMode?.mode_type === 'flash' && type === 'steady') {
            return false;
        }
        return true;
    }

    const [{ canDrop, isOver }, drop] = useDrop({
        accept: ["steady", "flash"], // Accept all types
        drop: (item: any, monitor) => {
            if (!isSameType(item)) {
                handleCannotDrop();
            } else {
                return { active: true, isDustbin: true };
            }
        },
        hover: (item, monitor) => {
            if (!monitor.isOver({ shallow: true })) return;
            if(!isSameType(item)) {
                setAllowedDrop(false);
            } else {
                setAllowedDrop(true);
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    });

    const { item, isDragging } = useDragLayer((monitor) => ({
        item: monitor.getItem(),
        isDragging: monitor.isDragging(),
    }));

    drop(ref);

    const backgroundColor = (isDragging && isSameType(item) && !item.active) ? styles['--theme-color'] : (canDrop && allowedDrop && isOver? styles['--theme-color']: 'transparent');
    const color = (isDragging && isSameType(item) && !item.active) ? styles['--theme-bg-color'] : (canDrop && allowedDrop && isOver? styles['--theme-bg-color']: styles['--theme-color']);
    const scale = (canDrop && allowedDrop && isOver && isSameType(item) ? 1.1 : 1);

    return (
        <Grid
            ref={ref}
            size={{ xs: 4, lg: 3 }}
            className={`add-new dustbin`}
            style={{ padding, backgroundColor, transform: `scale(${scale})`, transition: 'transform 0.1s ease-in-out' }}
        >{}
            {/* <PlusCircle invert={canDrop && allowedDrop && isOver} width={'30%'}/> */}
            <div className="add-new-label" style={{color: color}}>DRAG A</div>
            <svg 
                viewBox={type === 'steady' ? "0 0 20 20" : "0 0 9 9" }
                stroke={styles['--theme-color']} 
                fill={styles['--theme-color']}
                width='15%'
            >
                <path 
                    strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"
                    stroke={color}
                    d={
                        type === 'flash' ?
                        "M5 1L1 5.8H4.6L4.2 9L8.2 4.2H4.6L5 1Z" :
                        `M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 
                        0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 
                        14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 
                        0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 
                        0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 
                        1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z`
                    }
                />
            </svg>
            <div className="add-new-label" style={{color: color}}>MODE HERE</div>
        </Grid>
    );
};

export const ModesList = ({
    modes,
    handleDrop,
    deviceType
}: {
    modes: FlashMode[];
    handleDrop: Function;
    deviceType: number;
}) => {
    const [{ canDrop, isOver }, drop] = useDrop(() => ({
        accept: ["steady", "flash"],
        drop: () => ({ active: false, isDustbin: true }),
        collect: (monitor) => ({
            isOver: monitor.isOver(),
            canDrop: monitor.canDrop(),
        }),
    }));
    
    const { item, isDragging } = useDragLayer((monitor) => ({
        item: monitor.getItem(),
        isDragging: monitor.isDragging(),
    }));

    const className = `mode-list ${isDragging && item.active ? "hover" : ""}`;
    const border = (canDrop && isOver && item.active) ? '2px dashed var(--theme-color)' : '2px solid transparent';
    
    return (
        <Grid
            ref={drop}
            container
            spacing={2}
            rowGap={2}
            className={className}
            sx={{ margin: "20px 5px 5px 5px", minHeight: "100px", paddingBottom: "200px", border}}
        >
            {modes?.map((mode: FlashMode, index: number) => (
                <Grid size={{ xs: 3, lg: 2 }} key={mode.instance || `${mode.id}-${index}`}>
                    <Mode
                        mode={mode}
                        active={false}
                        handleDrop={handleDrop}
                        deviceType={deviceType}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

const Mode = ({
    mode,
    active,
    handleDrop,
    onClick,
    hasActive,
    deviceType,
}: {
    mode: FlashMode;
    active?: Boolean;
    handleDrop: Function;
    onClick?: Function;
    hasActive?: Boolean;
    deviceType: number;
}) => {
    const { getThemeStyles } = useGlobalContext();
    const styles = getThemeStyles();
    const [mouseOver, setMouseOver] = useState<boolean>(false);

    const ref = useRef<HTMLDivElement>(null);

    const [{ isDragging }, drag, preview] = useDrag(() => ({
        type: unifiedModes(deviceType) ? 'flash' : mode.mode_type,
        item: { flashMode: {...mode, mode_type: unifiedModes(deviceType) ? 'flash' : mode.mode_type}, active: active },
        end: (item, monitor) => {
            const dropResult = monitor.getDropResult<DropResult>();
            if (!dropResult || dropResult.active === active) {
                return;
            }
            if (dropResult.isDustbin) handleDrop(item.flashMode, dropResult.active);
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    }));
    
    useEffect(() => {
        if (ref.current) {
            preview(ref.current, { captureDraggingState: true });
        }
    }, [preview]);

    const [{ handlerId }, drop] = useDrop<
        DragItem,
        void,
        { handlerId: Identifier | null }
    >({
        accept: ["steady", "flash"],
        collect(monitor) {
            return { handlerId: monitor.getHandlerId() };
        },
        hover(item: DragItem, monitor) {
            if (!ref.current || item.active !== active) return;
        },
    });

    drag(drop(ref));

    const showFlashIcon = mode.mode_type !== 'steady' || unifiedModes(deviceType);
    const hasSettings = mode.allow_custom || mode.brake_patterns?.length || mode.tilt || mode.eye_saver;

    return (
        <div
            ref={ref}
            onClick={() => {if(hasSettings && onClick) onClick(mode, active)}}
            style={{
                opacity: isDragging ? 0.1 : 1,
                cursor: active ? "pointer" : "move",
            }}
            className="mode"
            onMouseEnter={() => setMouseOver(true)}
            onMouseLeave={() => setMouseOver(false)}
            data-handler-id={handlerId}
        >
            <div className={`mode-image ${hasActive ? "active" : "library"}`}>
                {active && hasSettings && mouseOver && (
                    <div className="configure-label">CONFIGURE</div>
                )}
                <Flash 
                    pattern={mode.config?.eyeSaver ? mode.eye_saver_pattern : mode.flash_pattern} 
                    multipliers={{brightness: mode.config?.brightness, speed: mode.config?.speed }} 
                    type={deviceType}
                />
                {mode.tilt && <div className="tilt-label">TILT</div>}
            </div>
            <div className={`mode-label ${hasActive ? "active" : "library"}`}>
                <div style={{padding: '5px 10px 10px 10px', width: '70%'}}>{mode.name}</div>
                <svg 
                    style={{ position: 'absolute', top: !showFlashIcon ? '24px' : '25px', right: '3px' }}
                    viewBox={!showFlashIcon ? "0 0 24 24" : "0 0 10 15"}
                    stroke={!hasActive ? styles['--theme-color'] : styles['--theme-bg-color']} 
                    width="18" 
                    height="20" 
                    fill='none'
                >
                    <path 
                        strokeWidth="0.8" strokeLinecap="round" strokeLinejoin="round"
                        d={
                            showFlashIcon ?
                            "M5 1L1 5.8H4.6L4.2 9L8.2 4.2H4.6L5 1Z" :
                            `M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 
                            0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 
                            14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 
                            0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 
                            0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 
                            1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z`
                        }
                    />
                </svg>
            </div>
        </div>
    );
};
